import React, { useId, useLayoutEffect, useMemo, useRef, useState } from 'react';

/**
 * BarChart — responsive, dependency-free SVG column chart for daily counts.
 *
 * Same sizing strategy as AreaChart: the wrapper is measured with a
 * ResizeObserver and the viewBox width tracks it, so 1 user unit === 1 CSS px
 * and text renders at true size (no preserveAspectRatio="none").
 *
 * Palette is locked to the BMLC system: bmlc-navy / bmlc-gold / bmlc-maroon.
 */

export interface BarChartDatum {
  label: string;
  value: number;
}

export interface BarChartProps {
  data: BarChartDatum[];
  height?: number;
  color?: 'navy' | 'gold' | 'maroon';
}

/* --- BMLC tokens (fixed palette) ------------------------------------------ */
const SERIES: Record<'navy' | 'gold' | 'maroon', { top: string; bottom: string; key: string }> = {
  navy: { top: '#1e3a5f', bottom: '#102238', key: '#1e3a5f' }, // navy-500 -> navy-800
  gold: { top: '#d4a017', bottom: '#966f08', key: '#b4860b' }, // gold-500 -> gold-700
  maroon: { top: '#800020', bottom: '#570014', key: '#800020' }, // maroon-500 -> maroon-700
};

const GRID = '#e0e9f2'; // bmlc-navy-100
const AXIS = '#c5d4e5'; // bmlc-navy-200
const INK = '#6b7280'; // gray-500 — 4.83:1
const BAND_WASH = 'rgba(30, 58, 95, 0.05)';

const fmt = (n: number): string =>
  String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

const safe = (n: number): number => (Number.isFinite(n) && n > 0 ? n : 0);

/** Nice rounded y-axis maximum with whole-number ticks. All-zero data -> max 4. */
function niceScale(max: number): { yMax: number; ticks: number[] } {
  if (!(max > 0)) return { yMax: 4, ticks: [0, 1, 2, 3, 4] };
  const MANT = [1, 1.5, 2, 2.5, 3, 4, 5, 7.5, 10];
  const COUNTS = [4, 5];
  let bestMax = Infinity;
  let bestCount = 4;
  for (let ci = 0; ci < COUNTS.length; ci++) {
    const count = COUNTS[ci];
    const target = max / count;
    const exp = Math.floor(Math.log(target) / Math.LN10);
    const pow = Math.pow(10, exp);
    let step = pow * 10;
    for (let i = 0; i < MANT.length; i++) {
      const s = MANT[i] * pow;
      if (s >= target - 1e-9) {
        step = s;
        break;
      }
    }
    step = step < 1 ? 1 : Math.ceil(step);
    const yMax = step * count;
    if (yMax < bestMax) {
      bestMax = yMax;
      bestCount = count;
    }
  }
  const ticks: number[] = [];
  for (let i = 0; i <= bestCount; i++) ticks.push((bestMax / bestCount) * i);
  return { yMax: bestMax, ticks };
}

/** Column with rounded top corners, square where it meets the baseline. */
function columnPath(x: number, y: number, w: number, h: number, r: number): string {
  const rr = Math.max(0, Math.min(r, w / 2, h));
  const bottom = y + h;
  return (
    `M${x.toFixed(2)},${bottom.toFixed(2)}` +
    ` L${x.toFixed(2)},${(y + rr).toFixed(2)}` +
    ` Q${x.toFixed(2)},${y.toFixed(2)} ${(x + rr).toFixed(2)},${y.toFixed(2)}` +
    ` L${(x + w - rr).toFixed(2)},${y.toFixed(2)}` +
    ` Q${(x + w).toFixed(2)},${y.toFixed(2)} ${(x + w).toFixed(2)},${(y + rr).toFixed(2)}` +
    ` L${(x + w).toFixed(2)},${bottom.toFixed(2)} Z`
  );
}

function useContainerWidth(fallback: number): [React.RefObject<HTMLDivElement>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number>(fallback);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const next = el.clientWidth;
      if (next > 0) setWidth(next);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

const BarChart: React.FC<BarChartProps> = ({ data, height = 220, color = 'navy' }) => {
  const rawId = useId();
  const uid = rawId.replace(/[^a-zA-Z0-9_-]/g, '');
  const [wrapRef, measured] = useContainerWidth(640);
  const svgRef = useRef<SVGSVGElement>(null);
  const [active, setActive] = useState<number | null>(null);

  const tone = SERIES[color];
  const w = Math.max(200, measured);
  const h = Math.max(140, height);
  const padT = 14;
  const padB = 26;
  const padR = 14;
  const padL = w < 360 ? 32 : 44;
  const plotW = Math.max(10, w - padL - padR);
  const plotH = Math.max(10, h - padT - padB);
  const baseY = padT + plotH;
  const tickFont = w < 360 ? 9 : 10;

  const points = useMemo(
    () => data.map((d) => ({ label: String(d.label), value: safe(d.value) })),
    [data]
  );
  const n = points.length;

  const { yMax, ticks, allZero } = useMemo(() => {
    let max = 0;
    for (let i = 0; i < points.length; i++) if (points[i].value > max) max = points[i].value;
    const scale = niceScale(max);
    return { yMax: scale.yMax, ticks: scale.ticks, allZero: points.length > 0 && max === 0 };
  }, [points]);

  const band = n > 0 ? plotW / n : plotW;
  // Cap the bar so the band always keeps air: the >= 2px surface gap between
  // neighbours comes from the leftover, never from a stroke.
  const barW = Math.max(2, Math.min(24, band - Math.max(3, band * 0.28)));
  const yAt = (v: number): number => baseY - (Math.min(v, yMax) / yMax) * plotH;

  const labelStride = Math.max(1, Math.ceil(n / 7));
  const activePoint = active !== null && active >= 0 && active < n ? points[active] : null;

  const summary = useMemo(() => {
    if (n === 0) return 'Bar chart — no data for this period';
    let total = 0;
    let peak = points[0];
    for (let i = 0; i < n; i++) {
      total += points[i].value;
      if (points[i].value > peak.value) peak = points[i];
    }
    return `Bar chart across ${n} ${n === 1 ? 'period' : 'periods'}, from ${
      points[0].label
    } to ${points[n - 1].label}. Total ${fmt(total)}, highest ${fmt(peak.value)} on ${peak.label}.`;
  }, [points, n]);

  const bandX = (i: number): number => padL + i * band;
  const tipX = activePoint ? Math.max(64, Math.min(w - 64, bandX(active as number) + band / 2)) : 0;
  const tipY = activePoint ? Math.max(46, yAt(activePoint.value)) : 0;

  return (
    <div ref={wrapRef} className="relative w-full">
      <svg
        ref={svgRef}
        role="img"
        aria-label={summary}
        viewBox={`0 0 ${w} ${h}`}
        style={{ display: 'block', width: '100%', height: h }}
        onPointerLeave={() => setActive(null)}
      >
        <defs>
          <linearGradient id={`bar-${uid}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={tone.top} />
            <stop offset="100%" stopColor={tone.bottom} />
          </linearGradient>
        </defs>

        {/* Gridlines */}
        {ticks.map((t) => {
          const y = yAt(t);
          return (
            <g key={`grid-${t}`}>
              <line
                x1={padL}
                y1={y}
                x2={padL + plotW}
                y2={y}
                stroke={t === 0 ? AXIS : GRID}
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
                shapeRendering="crispEdges"
              />
              <text
                x={padL - 8}
                y={y}
                dy="0.32em"
                textAnchor="end"
                fontSize={tickFont}
                fill={INK}
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {fmt(t)}
              </text>
            </g>
          );
        })}

        {/* Hovered band wash — drawn under the bars */}
        {activePoint && (
          <rect
            x={bandX(active as number)}
            y={padT}
            width={band}
            height={plotH}
            fill={BAND_WASH}
            pointerEvents="none"
          />
        )}

        {/* Columns */}
        {points.map((p, i) => {
          if (p.value <= 0) return null;
          const y = yAt(p.value);
          const barH = baseY - y;
          const x = bandX(i) + (band - barW) / 2;
          return (
            <path
              key={`bar-${i}`}
              d={columnPath(x, y, barW, barH, 4)}
              fill={`url(#bar-${uid})`}
              opacity={active === null || active === i ? 1 : 0.8}
              pointerEvents="none"
            />
          );
        })}

        {/* x-axis labels, thinned to ~7 */}
        {points.map((p, i) => {
          if (i % labelStride !== 0) return null;
          return (
            <text
              key={`xl-${i}`}
              x={bandX(i) + band / 2}
              y={baseY + 16}
              textAnchor="middle"
              fontSize={tickFont}
              fill={INK}
            >
              {p.label}
            </text>
          );
        })}

        {/* Hit targets — full plot height, full band width (bigger than the mark) */}
        {points.map((p, i) => (
          <rect
            key={`hit-${i}`}
            x={bandX(i)}
            y={padT}
            width={band}
            height={plotH}
            fill="transparent"
            onPointerEnter={() => setActive(i)}
            onPointerDown={() => setActive(i)}
          />
        ))}

        {(n === 0 || allZero) && (
          <text
            x={padL + plotW / 2}
            y={padT + plotH / 2}
            textAnchor="middle"
            dy="0.32em"
            fontSize={12}
            fill="#9ca3af"
          >
            {n === 0 ? 'No data for this period' : 'No activity in this period'}
          </text>
        )}
      </svg>

      {/* Tooltip */}
      {activePoint && (
        <div
          className="pointer-events-none absolute z-10 rounded-lg border border-bmlc-navy-100 bg-white/95 px-2.5 py-1.5 shadow-soft backdrop-blur-sm"
          style={{
            left: tipX,
            top: tipY - 10,
            transform: 'translate(-50%, -100%)',
            minWidth: 92,
          }}
        >
          <div className="flex items-center gap-1.5">
            <span
              className="inline-block h-2 w-2 rounded-sm"
              style={{ backgroundColor: tone.key }}
            />
            <span className="font-serif text-base font-bold leading-none text-bmlc-navy-900">
              {fmt(activePoint.value)}
            </span>
          </div>
          <div className="mt-0.5 whitespace-nowrap text-[11px] text-gray-600">
            {activePoint.label}
          </div>
        </div>
      )}

      {/* Table twin */}
      {n > 0 && (
        <table className="sr-only">
          <caption>{summary}</caption>
          <thead>
            <tr>
              <th scope="col">Period</th>
              <th scope="col">Count</th>
            </tr>
          </thead>
          <tbody>
            {points.map((p, i) => (
              <tr key={`row-${i}`}>
                <th scope="row">{p.label}</th>
                <td>{fmt(p.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default BarChart;
